import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric';
import { platform } from './platform';

/**
 * Thin wrapper over `@capgo/capacitor-native-biometric`. Centralises
 * availability detection (with a human label for the device's biometry)
 * and a single verify call used by both the Settings opt-in and the
 * app-launch unlock gate.
 */

export interface BiometryInfo {
  /** Hardware present + enrolled, and we're on a native platform. */
  available: boolean;
  /** Human label for the device's biometry — "Face ID", "Touch ID", … */
  label: string;
  type: BiometryType;
}

function labelFor(type: BiometryType): string {
  switch (type) {
    case BiometryType.FACE_ID:
      return 'Face ID';
    case BiometryType.TOUCH_ID:
      return 'Touch ID';
    case BiometryType.FINGERPRINT:
      return 'Fingerprint';
    case BiometryType.FACE_AUTHENTICATION:
      return 'Face unlock';
    case BiometryType.IRIS_AUTHENTICATION:
      return 'Iris unlock';
    case BiometryType.MULTIPLE:
      return 'Biometrics';
    default:
      return 'Biometrics';
  }
}

/** Detect whether biometric auth is available + which kind. Never throws. */
export async function getBiometry(): Promise<BiometryInfo> {
  if (!platform.isNative()) {
    return { available: false, label: 'Biometrics', type: BiometryType.NONE };
  }
  try {
    const res = await NativeBiometric.isAvailable({ useFallback: true });
    return {
      available: !!res.isAvailable,
      label: labelFor(res.biometryType),
      type: res.biometryType,
    };
  } catch {
    return { available: false, label: 'Biometrics', type: BiometryType.NONE };
  }
}

/**
 * Prompt the device biometric (with device-passcode fallback). Resolves
 * `true` on success, `false` on cancel/failure — callers decide what to do.
 */
export async function verifyBiometric(reason: string): Promise<boolean> {
  if (!platform.isNative()) return false;
  try {
    await NativeBiometric.verifyIdentity({
      reason,
      title: 'Unlock',
      subtitle: '',
      description: reason,
      useFallback: true,
    });
    return true;
  } catch {
    return false;
  }
}
